import { useEffect, useState, useCallback, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import { C2S, S2C } from '../multiplayer/events.ts';
import type { PresenceUser, ChallengeConfig } from '../multiplayer/types.ts';

/** Safety net: drop a pending spinner if the server never resolves the invite. */
const PENDING_FALLBACK_MS = 35_000;

export interface UsePresenceReturn {
  online: PresenceUser[];
  /** Target userIds we have an outstanding challenge to (show a spinner). */
  pending: Set<string>;
  sendChallenge: (targetUserId: string, config: ChallengeConfig) => void;
  refresh: () => void;
  /** Our own DB user id (from the `user:profile` handshake), null until known. */
  selfUserId: string | null;
  /** Our own presence row (from `user:profile`), shown at the top of the list. */
  self: PresenceUser | null;
}

/**
 * Tracks the list of online players (excluding self) by subscribing to
 * presence broadcasts, and exposes a helper to challenge a player plus the set
 * of players we are currently waiting on.
 */
export function usePresence(socket: Socket | null, _selfAuth0Sub: string | null = null): UsePresenceReturn {
  const [online, setOnline] = useState<PresenceUser[]>([]);
  const [pending, setPending] = useState<Set<string>>(new Set());
  // Our own DB user id, learned from the `user:profile` event the server emits
  // on connect. The presence list keys off DB ids, so this is what UserBadge
  // compares against to light up our own dot (the Auth0 `sub` won't match).
  const [selfUserId, setSelfUserId] = useState<string | null>(null);
  const [self, setSelf] = useState<PresenceUser | null>(null);
  const selfUserIdRef = useRef<string | null>(null);

  // challengeId → targetUserId, so resolution events (declined/cancelled/
  // accepted) that only carry a challengeId can clear the right spinner.
  const idToTargetRef = useRef<Map<string, string>>(new Map());
  const fallbackTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const clearPending = useCallback((targetUserId: string) => {
    setPending((prev) => {
      if (!prev.has(targetUserId)) return prev;
      const next = new Set(prev);
      next.delete(targetUserId);
      return next;
    });
    const timer = fallbackTimersRef.current.get(targetUserId);
    if (timer) {
      clearTimeout(timer);
      fallbackTimersRef.current.delete(targetUserId);
    }
  }, []);

  // ── Self identity ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;
    const handleProfile = (p: {
      userId: string; username: string; country: string; elo: number;
      avatarFilename?: string | null; playerCardDecoration?: string;
    }) => {
      selfUserIdRef.current = p.userId;
      setSelfUserId(p.userId);
      setSelf({
        userId: p.userId,
        username: p.username,
        country: p.country,
        elo: p.elo,
        avatarFilename: p.avatarFilename ?? null,
        playerCardDecoration: p.playerCardDecoration ?? 'none',
        acceptChallenges: true,
        inGame: false,
      });
    };
    socket.on(S2C.USER_PROFILE, handleProfile);
    return () => { socket.off(S2C.USER_PROFILE, handleProfile); };
  }, [socket]);

  // ── Online list ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const handleList = (list: PresenceUser[]) => setOnline(list);

    const handleOnline = (user: PresenceUser) => {
      // Server broadcasts in-game refreshes to the whole lobby, including the
      // affected user's own other tabs — never list ourselves.
      if (user.userId === selfUserIdRef.current) return;
      setOnline((prev) => {
        const idx = prev.findIndex((u) => u.userId === user.userId);
        if (idx === -1) return [...prev, user];
        // Existing row — refresh (e.g. acceptChallenges/inGame changed).
        const next = [...prev];
        next[idx] = user;
        return next;
      });
    };

    const handleOffline = ({ userId }: { userId: string }) => {
      setOnline((prev) => prev.filter((u) => u.userId !== userId));
    };

    socket.on(S2C.PRESENCE_LIST, handleList);
    socket.on(S2C.PRESENCE_ONLINE, handleOnline);
    socket.on(S2C.PRESENCE_OFFLINE, handleOffline);

    socket.emit(C2S.PRESENCE_LIST);

    return () => {
      socket.off(S2C.PRESENCE_LIST, handleList);
      socket.off(S2C.PRESENCE_ONLINE, handleOnline);
      socket.off(S2C.PRESENCE_OFFLINE, handleOffline);
    };
  }, [socket]);

  // ── Outgoing-challenge tracking ──────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;
    const map = idToTargetRef.current;

    const resolveById = (challengeId: string) => {
      const target = map.get(challengeId);
      if (!target) return;
      map.delete(challengeId);
      clearPending(target);
    };

    const handleSent = ({ challengeId, targetUserId }: { challengeId: string; targetUserId: string }) => {
      map.set(challengeId, targetUserId);
      setPending((prev) => (prev.has(targetUserId) ? prev : new Set(prev).add(targetUserId)));
    };
    const handleDeclined = ({ challengeId }: { challengeId: string }) => resolveById(challengeId);
    const handleCancelled = ({ challengeId }: { challengeId: string }) => resolveById(challengeId);
    const handleAccepted = ({ challengeId }: { challengeId: string }) => resolveById(challengeId);
    const handleFailed = ({ targetUserId }: { targetUserId?: string }) => {
      if (targetUserId) clearPending(targetUserId);
    };

    socket.on(S2C.CHALLENGE_SENT, handleSent);
    socket.on(S2C.CHALLENGE_DECLINED, handleDeclined);
    socket.on(S2C.CHALLENGE_CANCELLED, handleCancelled);
    socket.on(S2C.CHALLENGE_ACCEPTED, handleAccepted);
    socket.on(S2C.CHALLENGE_FAILED, handleFailed);

    return () => {
      socket.off(S2C.CHALLENGE_SENT, handleSent);
      socket.off(S2C.CHALLENGE_DECLINED, handleDeclined);
      socket.off(S2C.CHALLENGE_CANCELLED, handleCancelled);
      socket.off(S2C.CHALLENGE_ACCEPTED, handleAccepted);
      socket.off(S2C.CHALLENGE_FAILED, handleFailed);
    };
  }, [socket, clearPending]);

  // Clear any outstanding fallback timers on unmount.
  useEffect(() => {
    const timers = fallbackTimersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const sendChallenge = useCallback(
    (targetUserId: string, config: ChallengeConfig) => {
      if (!socket) return;
      socket.emit(C2S.CHALLENGE_SEND, { targetUserId, config });
      // Optimistically show the spinner; confirmed/cleared by server events.
      setPending((prev) => (prev.has(targetUserId) ? prev : new Set(prev).add(targetUserId)));
      const existing = fallbackTimersRef.current.get(targetUserId);
      if (existing) clearTimeout(existing);
      fallbackTimersRef.current.set(
        targetUserId,
        setTimeout(() => clearPending(targetUserId), PENDING_FALLBACK_MS),
      );
    },
    [socket, clearPending],
  );

  const refresh = useCallback(() => {
    socket?.emit(C2S.PRESENCE_LIST);
  }, [socket]);

  return { online, pending, sendChallenge, refresh, selfUserId, self };
}
